import React, { Component } from 'react';
import { Spinner, Badge } from 'react-bootstrap';
import { withApollo } from 'react-apollo';
import { GET_WHATSAPP_CHATS, GET_WHATSAPP_LEAD_TIMELINE } from '../../utils/whatsappHelper';

class TimelineViewer extends Component {
    constructor(props) {
        super(props);
        this.state = {
            view: 'whatsapp', // 'whatsapp' or 'leads'
            loading: false,
            timeline: [],
            page: 1,
            hasMore: true
        };
        this.timelineEndRef = React.createRef();
    }

    componentDidMount() {
        if (this.props.mobile || this.props.contactId) {
            this.fetchTimeline();
        }
    }

    componentDidUpdate(prevProps, prevState) {
        if (prevProps.mobile !== this.props.mobile || prevProps.contactId !== this.props.contactId) {
            this.setState({ timeline: [], page: 1, hasMore: true }, () => {
                this.fetchTimeline();
            });
        }
        if (prevState.view !== this.state.view) {
            this.setState({ timeline: [], page: 1, hasMore: true }, () => {
                this.fetchTimeline();
            });
        }
        this.scrollToBottom();
    }

    scrollToBottom = () => {
        if (this.timelineEndRef.current) {
            this.timelineEndRef.current.scrollIntoView({ behavior: "smooth" });
        }
    }

    fetchTimeline = async () => {
        const { mobile, contactId, employeeId, client } = this.props;
        const { view, page } = this.state;

        if (!employeeId || (!mobile && !contactId)) return;

        this.setState({ loading: true });
        try {
            let result;
            if (view === 'whatsapp') {
                result = await client.query({
                    query: GET_WHATSAPP_CHATS,
                    variables: { employee_id: parseInt(employeeId), mobile: String(mobile), page, limit: 20 },
                    fetchPolicy: 'network-only'
                });
                
                let data = result.data.getWhatsAppChats;
                if (typeof data === 'string') data = JSON.parse(data);
                
                const messages = data?.data?.messages || data?.data?.chats || [];
                this.setState(prev => ({
                    timeline: page === 1 ? [...messages].reverse() : [...[...messages].reverse(), ...prev.timeline],
                    hasMore: messages.length === 20,
                    loading: false
                }));
            } else {
                result = await client.query({
                    query: GET_WHATSAPP_LEAD_TIMELINE,
                    variables: { 
                        employee_id: parseInt(employeeId), 
                        mobile: mobile ? String(mobile) : null, 
                        wa_contact_id: contactId ? parseInt(contactId) : null 
                    },
                    fetchPolicy: 'network-only'
                });

                let data = result.data.getWhatsAppLeadTimeline;
                if (typeof data === 'string') data = JSON.parse(data);
                
                const interactions = data?.data || [];
                this.setState({
                    timeline: interactions,
                    hasMore: false,
                    loading: false
                });
            }
        } catch (error) {
            console.error("Failed to fetch timeline:", error);
            this.setState({ loading: false });
        }
    }

    renderMedia = (message) => {
        if (!message.media_url) return null;
        
        const type = message.type || 'image';
        if (type === 'image') {
            return <img src={message.media_url} alt="WA Media" className="img-fluid rounded mt-2 border" style={{ maxHeight: '200px' }} />;
        } else if (type === 'video') {
            return <video src={message.media_url} controls className="img-fluid rounded mt-2 border" style={{ maxHeight: '200px' }} />;
        }
        return <a href={message.media_url} target="_blank" rel="noopener noreferrer" className="d-block mt-2 small text-primary">View Attachment</a>;
    }

    render() {
        const { view, timeline, loading } = this.state;
        const { mobile, studentName } = this.props;

        if (!mobile) {
            return (
                <div className="h-100 d-flex align-items-center justify-content-center text-muted italic p-5">
                    Select a recipient to view history.
                </div>
            );
        }

        return (
            <div className="cc-timeline-wrapper d-flex flex-column h-100 bg-white overflow-hidden">
                <style>{`
                    .af-chat-bubble { 
                        padding: 12px 16px;
                        border-radius: 18px; 
                        box-shadow: 0 2px 4px rgba(15, 23, 42, 0.05);
                        font-weight: 400;
                        line-height: 1.5;
                    }
                    .bubble-left { 
                        background: #f1f5f9; 
                        color: #1e293b;
                        border-bottom-left-radius: 4px; 
                    }
                    .bubble-right { 
                        background: #0f172a; 
                        color: #ffffff;
                        border-bottom-right-radius: 4px; 
                    }
                    .af-interaction-item:last-child { border-left: 0 !important; }
                    .badge-soft-primary { background-color: #f1f5f9; color: #475569; border: 1px solid #e2e8f0; border-radius: 6px; padding: 4px 8px; font-size: 10px; text-transform: uppercase; letter-spacing: 0.5px; }
                    .timeline-dot {
                        width: 10px; height: 10px; border-radius: 50%; background: #0f172a; border: 2px solid #fff; box-shadow: 0 0 0 4px rgba(15, 23, 42, 0.05);
                    }
                `}</style>
                
                <div className="cc-timeline-sub-header px-4 py-2 border-bottom d-flex justify-content-between align-items-center bg-white">
                    <div className="small text-muted font-weight-bold">ACADEMIC INTERACTION LOG</div>
                    <div className="af-shadcn-tabs mb-0 scale-90">
                        <button 
                            className={`af-tabs-trigger px-3 py-1 ${view === 'whatsapp' ? 'active' : ''}`}
                            onClick={() => this.setState({ view: 'whatsapp' })}
                        >
                            <i className="fab fa-whatsapp" />
                        </button>
                        <button 
                            className={`af-tabs-trigger px-3 py-1 ${view === 'leads' ? 'active' : ''}`}
                            onClick={() => this.setState({ view: 'leads' })}
                        >
                            <i className="fas fa-list-ul" />
                        </button>
                    </div>
                </div>

                <div className="cc-timeline-canvas flex-fill p-4 overflow-auto" style={{ scrollBehavior: 'smooth' }}>
                    {loading && timeline.length === 0 && (
                        <div className="text-center py-5">
                            <Spinner animation="border" variant="primary" size="sm" />
                            <p className="mt-2 small text-muted">Retrieving interaction history...</p>
                        </div>
                    )}

                    {timeline.length === 0 && !loading && (
                        <div className="text-center py-5 text-muted small px-5 opacity-50">
                            <i className="fas fa-ghost fa-3x mb-3" />
                            <p>No interaction records found for this student.</p>
                        </div>
                    )}

                    {view === 'whatsapp' ? (
                        timeline.map((msg, i) => {
                            const isMe = msg.direction === 'outbound' || msg.status === 'sent' || msg.status === 'delivered';
                            return (
                                <div key={i} className={`d-flex mb-4 cc-animate-fade ${isMe ? 'justify-content-end' : 'justify-content-start'}`}>
                                    <div 
                                        className={`af-chat-bubble ${isMe ? 'bubble-right' : 'bubble-left'}`}
                                        style={{ maxWidth: '80%' }}
                                    >
                                        <div className="message-content" style={{ fontSize: '13.5px', whiteSpace: 'pre-wrap' }}>{msg.message}</div>
                                        {this.renderMedia(msg)}
                                        <div className="mt-2 d-flex align-items-center justify-content-end" style={{ fontSize: '10px', opacity: 0.6 }}>
                                            <span>{new Date(msg.created_at || msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                                            {isMe && <i className={`fas fa-check-double ml-1 ${msg.status === 'read' ? 'text-info' : ''}`} />}
                                        </div>
                                    </div>
                                </div>
                            );
                        })
                    ) : (
                        <div className="cc-interaction-flow">
                            {timeline.map((item, i) => (
                                <div key={i} className="cc-flow-item position-relative pl-4 pb-4 border-left ml-2" style={{ borderColor: '#f1f5f9 !important' }}>
                                    <div className="timeline-dot position-absolute" style={{ left: '-6px', top: '2px' }} />
                                    <div className="d-flex justify-content-between mb-1 align-items-start">
                                        <span className="badge-soft-primary">{item.lead_status || 'Activity'}</span>
                                        <small className="text-muted" style={{ fontSize: '10px' }}>{new Date(item.updated_at).toLocaleDateString()}</small>
                                    </div>
                                    <p className="small text-dark mb-1 font-weight-500">{item.comments || 'Staff interaction registered.'}</p>
                                    <div className="d-flex align-items-center mt-1 opacity-60">
                                        <i className="fas fa-shield-check mr-2" style={{ fontSize: '10px' }} />
                                        <span style={{ fontSize: '10px' }}>Institutional Audit Log</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                    <div ref={this.timelineEndRef} />
                </div>
            </div>
        );
    }
}

export default withApollo(TimelineViewer);
